import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// shadcn UI components
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import { useToast } from '../../components/ui/use-toast';

// icons
import { 
  Profile2User, 
  Setting3, 
  SearchNormal1,
  Building,
  UserEdit,
  Save2
} from 'iconsax-react';

// project imports
import { 
  getPlans, 
  getOrganizations, 
  assignPlan, 
  updateOverrides 
} from '../../services/subscriptionService';
import UserService from '../../services/userService';

const OrgPlanAssignment = () => {
  const { toast } = useToast();
  const location = useLocation();

  // State
  const [plans, setPlans] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [csms, setCsms] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Form states
  const [planForm, setPlanForm] = useState({
    planId: '',
    planStatus: 'active',
    trialDays: '14',
    billingCycleMonths: '1',
    assignedCSM: '',
    reason: ''
  });

  const [overrideForm, setOverrideForm] = useState({
    maxAdmins: '',
    maxCaregivers: '',
    maxPatients: '',
    maxFileSizeMB: '',
    totalStorageGB: '',
    auditRetentionDays: '',
    reason: ''
  });

  // Load plans, orgs, and admin users (for CSM list)
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [plansRes, orgsRes, csmsRes] = await Promise.all([
        getPlans(),
        getOrganizations(),
        UserService.getAllUsers({ limit: 1000 })
      ]);

      const loadedPlans = plansRes.data || [];
      const loadedOrgs = orgsRes.data || [];
      const loadedCsms = csmsRes.data?.users || [];

      setPlans(loadedPlans);
      setOrgs(loadedOrgs);
      setCsms(loadedCsms);

      // Select first organization or match query parameter
      const params = new URLSearchParams(location.search);
      const initialOrgId = params.get('orgId');
      
      if (initialOrgId && loadedOrgs.find(o => o._id === initialOrgId)) {
        setSelectedOrgId(initialOrgId);
      } else if (loadedOrgs.length > 0) {
        setSelectedOrgId(loadedOrgs[0]._id);
      }

    } catch (error) {
      console.error('Error fetching assignment details:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load organizations or subscription plans."
      });
    } finally {
      setLoading(false);
    }
  };

  const selectedOrg = orgs.find(o => o._id === selectedOrgId);

  // Sync form states with selected organization details
  useEffect(() => {
    if (selectedOrg) {
      setPlanForm({
        planId: selectedOrg.planId?._id || '',
        planStatus: selectedOrg.planStatus || 'trialing',
        trialDays: selectedOrg.trialEndsAt ? 
          Math.max(0, Math.ceil((new Date(selectedOrg.trialEndsAt) - Date.now()) / (1000 * 60 * 60 * 24))).toString() : '14',
        billingCycleMonths: '1',
        assignedCSM: selectedOrg.assignedCSM?._id || selectedOrg.assignedCSM || '',
        reason: ''
      });

      setOverrideForm({
        maxAdmins: (selectedOrg.overrides?.maxAdmins !== undefined && selectedOrg.overrides?.maxAdmins !== null) ? selectedOrg.overrides.maxAdmins.toString() : '',
        maxCaregivers: (selectedOrg.overrides?.maxCaregivers !== undefined && selectedOrg.overrides?.maxCaregivers !== null) ? selectedOrg.overrides.maxCaregivers.toString() : '',
        maxPatients: (selectedOrg.overrides?.maxPatients !== undefined && selectedOrg.overrides?.maxPatients !== null) ? selectedOrg.overrides.maxPatients.toString() : '',
        maxFileSizeMB: (selectedOrg.overrides?.maxFileSizeMB !== undefined && selectedOrg.overrides?.maxFileSizeMB !== null) ? selectedOrg.overrides.maxFileSizeMB.toString() : '',
        totalStorageGB: (selectedOrg.overrides?.totalStorageGB !== undefined && selectedOrg.overrides?.totalStorageGB !== null) ? selectedOrg.overrides.totalStorageGB.toString() : '',
        auditRetentionDays: (selectedOrg.overrides?.auditRetentionDays !== undefined && selectedOrg.overrides?.auditRetentionDays !== null) ? selectedOrg.overrides.auditRetentionDays.toString() : '',
        reason: ''
      });
    }
  }, [selectedOrgId, orgs]);

  // Handle plan assignment save
  const handleAssignPlan = async (e) => {
    e.preventDefault();
    if (!planForm.planId) {
      toast({ variant: "destructive", title: "Validation Error", description: "Please select a plan." });
      return;
    }
    if (!planForm.reason.trim()) {
      toast({ variant: "destructive", title: "Validation Error", description: "Reason for assignment is required." });
      return;
    }

    try {
      setSubmitting(true);
      await assignPlan(selectedOrgId, planForm);
      toast({ title: "Success", description: "Subscription plan assigned successfully." });
      
      // Reload organization data
      const orgsRes = await getOrganizations();
      setOrgs(orgsRes.data || []);
    } catch (error) {
      console.error('Plan assignment error:', error);
      toast({
        variant: "destructive",
        title: "Error Assigning Plan",
        description: error.response?.data?.message || "Internal server error occurred."
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle overrides update
  const handleUpdateOverrides = async (e) => {
    e.preventDefault();
    if (!overrideForm.reason.trim()) {
      toast({ variant: "destructive", title: "Validation Error", description: "Reason for overrides update is required." });
      return;
    }

    try {
      setSubmitting(true);

      const parsedOverrides = {};
      const fields = ['maxAdmins', 'maxCaregivers', 'maxPatients', 'maxFileSizeMB', 'totalStorageGB', 'auditRetentionDays'];
      
      fields.forEach(field => {
        if (overrideForm[field] !== '') {
          const parsed = parseInt(overrideForm[field], 10);
          if (!isNaN(parsed)) {
            parsedOverrides[field] = parsed;
          } else {
            throw new Error(`Invalid number provided for ${field}`);
          }
        } else {
          parsedOverrides[field] = null; // clear override
        }
      });

      await updateOverrides(selectedOrgId, {
        overrides: parsedOverrides,
        reason: overrideForm.reason
      });

      toast({ title: "Success", description: "Limits overrides updated successfully." });
      
      // Reload organization data
      const orgsRes = await getOrganizations();
      setOrgs(orgsRes.data || []);
    } catch (error) {
      console.error('Overrides update error:', error);
      toast({
        variant: "destructive",
        title: "Error Updating Overrides",
        description: error.response?.data?.message || "Internal server error occurred."
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Filter organizations by search
  const filteredOrgs = orgs.filter(o => 
    o.name.toLowerCase().includes(search.toLowerCase()) || 
    o.code.toLowerCase().includes(search.toLowerCase())
  );

  // Calculate Limits health & percentages for progress bars
  const getLimitsQuotas = () => {
    if (!selectedOrg) return null;
    const plan = selectedOrg.planId;
    const overrides = selectedOrg.overrides || {};
    const usage = selectedOrg.currentUsage || { caregiverCount: 0, patientCount: 0, adminCount: 0 };

    const getVal = (field, planVal) => {
      if (overrides[field] !== undefined && overrides[field] !== null) return overrides[field];
      return planVal !== undefined ? planVal : -1;
    };

    const maxCaregivers = getVal('maxCaregivers', plan?.limits?.maxCaregivers);
    const maxPatients = getVal('maxPatients', plan?.limits?.maxPatients);
    const maxAdmins = getVal('maxAdmins', plan?.limits?.maxAdmins);

    const calcPercent = (curr, max) => {
      if (max === -1) return 0;
      return Math.min(100, Math.round((curr / max) * 100));
    };

    const getProgressColor = (percent) => {
      if (percent >= 90) return 'bg-rose-500';
      if (percent >= 75) return 'bg-amber-500';
      return 'bg-purple-600 dark:bg-purple-500';
    };

    return {
      caregivers: { current: usage.caregiverCount, max: maxCaregivers, percent: calcPercent(usage.caregiverCount, maxCaregivers), color: getProgressColor(calcPercent(usage.caregiverCount, maxCaregivers)) },
      patients: { current: usage.patientCount, max: maxPatients, percent: calcPercent(usage.patientCount, maxPatients), color: getProgressColor(calcPercent(usage.patientCount, maxPatients)) },
      admins: { current: usage.adminCount, max: maxAdmins, percent: calcPercent(usage.adminCount, maxAdmins), color: getProgressColor(calcPercent(usage.adminCount, maxAdmins)) },
    };
  };

  const limitsHealth = getLimitsQuotas();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* Sidebar List: Healthcare Agencies */}
      <Card className="lg:col-span-1 shadow-sm h-[calc(100vh-12rem)] flex flex-col">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Organizations</CardTitle>
          <CardDescription>Select agency to view configuration</CardDescription>
          <div className="relative mt-2">
            <SearchNormal1 size={18} className="absolute left-3 top-3 text-muted-foreground" />
            <Input 
              placeholder="Search agencies..." 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-10"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-y-auto flex-grow">
          {loading ? (
            <div className="p-6 text-center text-sm text-muted-foreground">Loading agencies...</div>
          ) : filteredOrgs.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">No agencies found</div>
          ) : (
            <div className="divide-y">
              {filteredOrgs.map((org) => {
                const isSelected = org._id === selectedOrgId;
                return (
                  <button
                    key={org._id}
                    onClick={() => setSelectedOrgId(org._id)}
                    className={`w-full text-left p-4 transition-colors hover:bg-slate-50 dark:hover:bg-slate-900/50 flex flex-col gap-1 ${
                      isSelected ? 'bg-purple-50/50 dark:bg-purple-950/10 border-l-4 border-l-purple-600' : ''
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <span className="font-semibold text-foreground text-sm line-clamp-1">{org.name}</span>
                      <Badge variant="outline" className="text-xs uppercase scale-90">
                        {org.planCode || 'no plan'}
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center text-xs text-muted-foreground mt-1">
                      <span>Code: {org.code}</span>
                      <span className={`capitalize ${
                        org.planStatus === 'active' ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'
                      }`}>{org.planStatus}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Main Content Area */}
      <div className="lg:col-span-3 space-y-6 overflow-y-auto h-[calc(100vh-12rem)] pr-2">
        {selectedOrg ? (
          <>
            {/* Overview / Header Card */}
            <Card className="shadow-sm">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-purple-50 dark:bg-purple-950/20 text-purple-600 dark:text-purple-400 rounded-xl">
                      <Building size={32} />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-foreground">{selectedOrg.name}</h2>
                      <div className="flex flex-wrap items-center gap-2 mt-1.5 text-sm text-muted-foreground">
                        <span>Code: <code className="bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded font-mono text-xs">{selectedOrg.code}</code></span>
                        <span>•</span>
                        <span>Domain: <code className="bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded font-mono text-xs">{selectedOrg.domain}</code></span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    <div className="flex gap-2">
                      <Badge className="bg-purple-50 text-purple-700 hover:bg-purple-50 dark:bg-purple-950/20 dark:text-purple-400 border border-purple-200">
                        {selectedOrg.planId?.planName || 'No Active Plan'}
                      </Badge>
                      <Badge className={
                        selectedOrg.planStatus === 'active' 
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : 'bg-amber-50 text-amber-700 border-amber-200'
                      }>
                        Status: {selectedOrg.planStatus}
                      </Badge>
                    </div>
                    {selectedOrg.trialEndsAt && selectedOrg.planStatus === 'trialing' && (
                      <span className="text-xs text-muted-foreground">
                        Trial ends: {new Date(selectedOrg.trialEndsAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Utilization health & parameters */}
            {limitsHealth && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Caregivers Util */}
                <Card className="shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center justify-between">
                      <span>Caregivers Utilization</span>
                      <Profile2User size={16} className="text-muted-foreground" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between items-end">
                      <span className="text-2xl font-bold">{limitsHealth.caregivers.current}</span>
                      <span className="text-sm text-muted-foreground">
                        / {limitsHealth.caregivers.max === -1 ? 'Unlimited' : limitsHealth.caregivers.max} Limit
                      </span>
                    </div>
                    {limitsHealth.caregivers.max !== -1 ? (
                      <>
                        <div className="w-full bg-slate-100 dark:bg-slate-800 h-2 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all ${limitsHealth.caregivers.color}`} 
                            style={{ width: `${limitsHealth.caregivers.percent}%` }}
                          />
                        </div>
                        <div className="text-right text-xs text-muted-foreground">
                          {limitsHealth.caregivers.percent}% utilized
                        </div>
                      </>
                    ) : (
                      <p className="text-xs text-emerald-600 dark:text-emerald-400">Unlimited caregivers allowed</p>
                    )}
                  </CardContent>
                </Card>

                {/* Patients Util */}
                <Card className="shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center justify-between">
                      <span>Patients Utilization</span>
                      <Profile2User size={16} className="text-muted-foreground" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between items-end">
                      <span className="text-2xl font-bold">{limitsHealth.patients.current}</span>
                      <span className="text-sm text-muted-foreground">
                        / {limitsHealth.patients.max === -1 ? 'Unlimited' : limitsHealth.patients.max} Limit
                      </span>
                    </div>
                    {limitsHealth.patients.max !== -1 ? (
                      <>
                        <div className="w-full bg-slate-100 dark:bg-slate-800 h-2 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all ${limitsHealth.patients.color}`} 
                            style={{ width: `${limitsHealth.patients.percent}%` }}
                          />
                        </div>
                        <div className="text-right text-xs text-muted-foreground">
                          {limitsHealth.patients.percent}% utilized
                        </div>
                      </>
                    ) : (
                      <p className="text-xs text-emerald-600 dark:text-emerald-400">Unlimited patients allowed</p>
                    )}
                  </CardContent>
                </Card>

                {/* Admins Util */}
                <Card className="shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center justify-between">
                      <span>Admins Utilization</span>
                      <Profile2User size={16} className="text-muted-foreground" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between items-end">
                      <span className="text-2xl font-bold">{limitsHealth.admins.current}</span>
                      <span className="text-sm text-muted-foreground">
                        / {limitsHealth.admins.max === -1 ? 'Unlimited' : limitsHealth.admins.max} Limit
                      </span>
                    </div>
                    {limitsHealth.admins.max !== -1 ? (
                      <>
                        <div className="w-full bg-slate-100 dark:bg-slate-800 h-2 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all ${limitsHealth.admins.color}`} 
                            style={{ width: `${limitsHealth.admins.percent}%` }}
                          />
                        </div>
                        <div className="text-right text-xs text-muted-foreground">
                          {limitsHealth.admins.percent}% utilized
                        </div>
                      </>
                    ) : (
                      <p className="text-xs text-emerald-600 dark:text-emerald-400">Unlimited admins allowed</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Form 1: Plan Assignment */}
              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <UserEdit size={20} className="text-primary" />
                    <span>Plan Assignment & Status</span>
                  </CardTitle>
                  <CardDescription>Change pricing tier and assign CSM agent support</CardDescription>
                </CardHeader>
                <form onSubmit={handleAssignPlan}>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="assignPlanId">Select Subscription Plan</Label>
                      <Select 
                        value={planForm.planId} 
                        onValueChange={(val) => setPlanForm(p => ({ ...p, planId: val }))}
                      >
                        <SelectTrigger id="assignPlanId">
                          <SelectValue placeholder="Select Plan Tier" />
                        </SelectTrigger>
                        <SelectContent>
                          {plans.map(p => (
                            <SelectItem key={p._id} value={p._id}>
                              {p.planName} (₹{p.price?.monthly}/mo)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="assignPlanStatus">Plan Status</Label>
                        <Select 
                          value={planForm.planStatus} 
                          onValueChange={(val) => setPlanForm(p => ({ ...p, planStatus: val }))}
                        >
                          <SelectTrigger id="assignPlanStatus">
                            <SelectValue placeholder="Select Status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">Active (Paid)</SelectItem>
                            <SelectItem value="trialing">Trialing</SelectItem>
                            <SelectItem value="suspended">Suspended</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {planForm.planStatus === 'trialing' ? (
                        <div className="space-y-2">
                          <Label htmlFor="trialDays">Trial Duration (Days)</Label>
                          <Input 
                            id="trialDays" 
                            type="number"
                            value={planForm.trialDays}
                            onChange={(e) => setPlanForm(p => ({ ...p, trialDays: e.target.value }))}
                          />
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Label htmlFor="billingCycle">Billing Cycle (Months)</Label>
                          <Input 
                            id="billingCycle" 
                            type="number"
                            value={planForm.billingCycleMonths}
                            onChange={(e) => setPlanForm(p => ({ ...p, billingCycleMonths: e.target.value }))}
                          />
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="assignedCSM">Assigned CSM Support Agent</Label>
                      <Select 
                        value={planForm.assignedCSM} 
                        onValueChange={(val) => setPlanForm(p => ({ ...p, assignedCSM: val }))}
                      >
                        <SelectTrigger id="assignedCSM">
                          <SelectValue placeholder="No CSM Assigned" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No CSM Assigned</SelectItem>
                          {csms.map(u => (
                            <SelectItem key={u._id} value={u._id}>
                              {u.name} ({u.email})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="planReason" className="flex items-center gap-1">
                        <span>Reason for Change</span>
                        <Badge variant="outline" className="text-[10px] uppercase font-bold text-rose-600 bg-rose-50 border-rose-300">Required</Badge>
                      </Label>
                      <Textarea 
                        id="planReason" 
                        placeholder="Must specify justification for upgrade, downgrade, or trial extension..." 
                        value={planForm.reason}
                        onChange={(e) => setPlanForm(p => ({ ...p, reason: e.target.value }))}
                        rows={3}
                        required
                      />
                    </div>
                  </CardContent>
                  <CardFooter className="border-t pt-4 bg-slate-50/50 dark:bg-slate-900/50 flex justify-end">
                    <Button 
                      type="submit" 
                      disabled={submitting}
                      className="bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-1.5"
                    >
                      <Save2 size={16} />
                      {submitting ? 'Saving...' : 'Apply Plan Info'}
                    </Button>
                  </CardFooter>
                </form>
              </Card>

              {/* Form 2: Custom Overrides */}
              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Setting3 size={20} className="text-primary" />
                    <span>Custom Limit Overrides</span>
                  </CardTitle>
                  <CardDescription>Adjust quotas specifically for this organization</CardDescription>
                </CardHeader>
                <form onSubmit={handleUpdateOverrides}>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="orCaregivers">Caregivers Quota</Label>
                        <Input 
                          id="orCaregivers" 
                          placeholder="Plan Default" 
                          value={overrideForm.maxCaregivers}
                          onChange={(e) => setOverrideForm(p => ({ ...p, maxCaregivers: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="orPatients">Patients Quota</Label>
                        <Input 
                          id="orPatients" 
                          placeholder="Plan Default" 
                          value={overrideForm.maxPatients}
                          onChange={(e) => setOverrideForm(p => ({ ...p, maxPatients: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="orAdmins">Admin Logins</Label>
                        <Input 
                          id="orAdmins" 
                          placeholder="Plan Default" 
                          value={overrideForm.maxAdmins}
                          onChange={(e) => setOverrideForm(p => ({ ...p, maxAdmins: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="orFileSize">Max File Size (MB)</Label>
                        <Input 
                          id="orFileSize" 
                          placeholder="Plan Default" 
                          value={overrideForm.maxFileSizeMB}
                          onChange={(e) => setOverrideForm(p => ({ ...p, maxFileSizeMB: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="orStorage">Storage Limit (GB)</Label>
                        <Input 
                          id="orStorage" 
                          placeholder="Plan Default" 
                          value={overrideForm.maxStorageGB || overrideForm.totalStorageGB}
                          onChange={(e) => setOverrideForm(p => ({ ...p, totalStorageGB: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="orRetention">Audit Trail (Days)</Label>
                        <Input 
                          id="orRetention" 
                          placeholder="Plan Default" 
                          value={overrideForm.auditRetentionDays}
                          onChange={(e) => setOverrideForm(p => ({ ...p, auditRetentionDays: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="overrideReason" className="flex items-center gap-1">
                        <span>Reason for Overrides</span>
                        <Badge variant="outline" className="text-[10px] uppercase font-bold text-rose-600 bg-rose-50 border-rose-300">Required</Badge>
                      </Label>
                      <Textarea 
                        id="overrideReason" 
                        placeholder="Must specify compliance or business contract reason for custom override limits..." 
                        value={overrideForm.reason}
                        onChange={(e) => setOverrideForm(p => ({ ...p, reason: e.target.value }))}
                        rows={3}
                        required
                      />
                    </div>
                  </CardContent>
                  <CardFooter className="border-t pt-4 bg-slate-50/50 dark:bg-slate-900/50 flex justify-end">
                    <Button 
                      type="submit" 
                      disabled={submitting}
                      className="bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-1.5"
                    >
                      <Save2 size={16} />
                      {submitting ? 'Saving...' : 'Apply Overrides'}
                    </Button>
                  </CardFooter>
                </form>
              </Card>
            </div>
          </>
        ) : (
          <div className="min-h-[400px] flex items-center justify-center border border-dashed rounded-xl bg-slate-50 dark:bg-slate-950/20 text-muted-foreground p-8 text-center">
            <div>
              <Building size={48} className="mx-auto text-muted-foreground mb-4 opacity-50" />
              <h3 className="font-semibold text-lg">No Organization Selected</h3>
              <p className="text-sm max-w-sm mt-1">Please select a healthcare agency from the left sidebar to assign plans and configure overrides.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrgPlanAssignment;
